import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { A, useNavigate, useParams } from "@solidjs/router";
import type { AlertRule, TaskStage } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatAgo, formatDateTime, formatNumber } from "../format.ts";
import { pathTo, siteTree } from "../context.ts";
import {
  conditionText,
  escalationText,
  eventTitle,
  ruleOf,
  userName,
} from "../events.ts";
import { pointSeries } from "../pointData.ts";
import { Icon } from "../icons.tsx";
import { Chart } from "../components/Chart.tsx";
import { unitLabel } from "../components/PointCard.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { AssignDialog, CloseDialog } from "../components/EventDialogs.tsx";
import { RuleWizard } from "../components/RuleWizard.tsx";
import { operator, Skeleton, useAction } from "../components/ui.tsx";
import { mediumColor } from "../components/Media.tsx";

const STAGES: readonly TaskStage[] = [
  "toAssign",
  "assigned",
  "inProgress",
  "toVerify",
  "closed",
];
const HOUR = 3_600_000;

/**
 * One event (prototype "zdarzenie"): condition with its occurrences, acknowledgement, task and escalation; the data
 * behind it; and the history of handling, where people add comments.
 */
export function EventPage() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, { refetch }] = createResource(
    () => params.id,
    (id) => api.events.get(id),
  );
  const [rules, { refetch: refetchRules }] = createResource(() =>
    api.events.rules(),
  );
  const [users] = createResource(() => api.events.users());
  const [comment, setComment] = createSignal("");
  const [dialog, setDialog] = createSignal<"assign" | "close" | "rule">();
  const [run, error, busy] = useAction();
  const e = () => detail()?.event;
  const rule = createMemo(() => (e() ? ruleOf(e()!, rules()) : undefined));
  const reload = async () => {
    api.invalidate(`/api/v1/events/${encodeURIComponent(params.id)}`);
    api.invalidate("/api/v1/events");
    await refetch();
  };
  const act = (f: () => Promise<unknown>) =>
    void run(async () => {
      await f();
      await reload();
    });

  return (
    <Show when={!detail.error} fallback={<ErrorBox error={detail.error} />}>
      <Show when={e()} fallback={<Skeleton lines={8} />}>
        {(ev) => {
          const closed = () => ev().stage === "closed";
          const esc = () => escalationText(ev(), rule(), users());
          const point = () =>
            ev().subjectType === "point"
              ? siteTree()?.points.find((p) => p.id === ev().subjectId)
              : undefined;
          const place = () =>
            pathTo(siteTree()?.nodes ?? [], ev().nodeId ?? undefined)
              .map((n) => n.name)
              .join(" / ");
          return (
            <>
              <div class="ph">
                <div>
                  <button
                    type="button"
                    class="btn ghost"
                    style={{ "padding-left": 0 }}
                    onClick={() => navigate("/events")}
                  >
                    <Icon name="chevl" />
                    {t("events.pageTitle")}
                  </button>
                  <div class="eyebrow">
                    {t(`events.kind.${ev().kind}`)} · {ev().subjectName}
                  </div>
                  <h1>{eventTitle(ev(), rule())}</h1>
                  <div class="sub">
                    {t("events.sinceAt", {
                      at: formatDateTime(ev().startedAt, locale()),
                    })}
                    {ev().endedAt
                      ? ` · ${t("events.endedAt", { at: formatDateTime(ev().endedAt, locale()) })}`
                      : ` · ${t("events.since", { ago: formatAgo(ev().startedAt, locale()) })}`}
                    <Show when={rule()}>
                      {(r) => (
                        <>
                          {" · "}
                          {t("events.ruleLabel")}{" "}
                          <button
                            type="button"
                            class="btn ghost sm"
                            disabled={!operator()}
                            onClick={() => setDialog("rule")}
                          >
                            {r().name}
                          </button>
                        </>
                      )}
                    </Show>
                    <Show when={ev().test}>
                      {" "}
                      <span class="chip acc">{t("events.test")}</span>
                    </Show>
                  </div>
                </div>
                <div class="acts">
                  <Show when={!ev().acknowledgedAt}>
                    <button
                      type="button"
                      class="btn"
                      disabled={busy()}
                      onClick={() =>
                        act(() => api.events.acknowledge([ev().id]))
                      }
                    >
                      <Icon name="check" />
                      {t("events.acknowledge")}
                    </button>
                  </Show>
                  <button
                    type="button"
                    class="btn"
                    onClick={() => setDialog("assign")}
                  >
                    <Icon name="users" />
                    {t("events.assignMany")}
                  </button>
                  <Show
                    when={closed()}
                    fallback={
                      <button
                        type="button"
                        class="btn"
                        onClick={() => setDialog("close")}
                      >
                        <Icon name="check" />
                        {t("events.closeTask")}
                      </button>
                    }
                  >
                    <button
                      type="button"
                      class="btn"
                      disabled={busy()}
                      onClick={() =>
                        act(() =>
                          api.events.task([ev().id], {
                            stage: ev().assignee ? "assigned" : "toAssign",
                            note: t("events.reopened"),
                          }),
                        )
                      }
                    >
                      {t("events.reopen")}
                    </button>
                  </Show>
                  <Show when={rule()}>
                    {(r) => (
                      <button
                        type="button"
                        class="btn"
                        disabled={busy()}
                        onClick={() =>
                          void run(async () => {
                            await api.events.muteRule(
                              r().id,
                              r().mutedUntil &&
                                new Date(r().mutedUntil!).getTime() > Date.now()
                                ? 0
                                : 120,
                            );
                            api.invalidate("/api/v1/alert-rules");
                            await refetchRules();
                          })
                        }
                      >
                        <Icon name="pause" />
                        {r().mutedUntil &&
                        new Date(r().mutedUntil!).getTime() > Date.now()
                          ? t("events.unmute", {
                              at: formatDateTime(r().mutedUntil, locale()),
                            })
                          : t("events.mute2h")}
                      </button>
                    )}
                  </Show>
                </div>
              </div>
              <Show when={error()}>
                <ErrorBox error={error()} />
              </Show>
              <div class="axes">
                <div class="axis card">
                  <span class="eyebrow">{t("events.th.condition")}</span>
                  <span class={`chip ${ev().endedAt === null ? "crit" : "ok"}`}>
                    {ev().endedAt === null
                      ? t("events.cond.on")
                      : t("events.cond.off")}
                  </span>
                  <span class="q">
                    {ev().occurrences > 1
                      ? t("events.occurrences", { count: ev().occurrences })
                      : t("events.oneOccurrence")}
                  </span>
                </div>
                <div class="axis card">
                  <span class="eyebrow">{t("events.th.ack")}</span>
                  <span class={`chip ${ev().acknowledgedAt ? "" : "warn"}`}>
                    {ev().acknowledgedAt
                      ? t("events.ack.yes")
                      : t("events.ack.no")}
                  </span>
                  <span class="q">
                    {ev().acknowledgedAt
                      ? `${ev().acknowledgedBy} · ${formatDateTime(ev().acknowledgedAt, locale())}`
                      : t("events.nobodyYet")}
                  </span>
                </div>
                <div class="axis card">
                  <span class="eyebrow">{t("events.th.task")}</span>
                  <select
                    aria-label={t("events.th.task")}
                    class="inp"
                    value={ev().stage}
                    disabled={busy()}
                    onChange={(x) =>
                      act(() =>
                        api.events.task([ev().id], {
                          stage: x.currentTarget.value as TaskStage,
                        }),
                      )
                    }
                  >
                    <For each={STAGES}>
                      {(s) => (
                        <option value={s}>{t(`events.stage.${s}`)}</option>
                      )}
                    </For>
                  </select>
                  <span class="q">
                    {userName(users(), ev().assignee)}
                    {ev().due
                      ? ` · ${t("events.dueOn", { date: new Intl.DateTimeFormat(locale(), { dateStyle: "medium" }).format(new Date(`${ev().due}T12:00:00`)) })}`
                      : ""}
                  </span>
                </div>
                <div class="axis card">
                  <span class="eyebrow">{t("events.escalation")}</span>
                  <span style={{ "font-weight": 500 }}>{esc()[0]}</span>
                  <span class="q">{esc()[1]}</span>
                </div>
                <div class="axis card">
                  <span class="eyebrow">{t("events.value")}</span>
                  <span class="num">
                    {ev().value === null
                      ? "—"
                      : formatNumber(ev().value, locale(), 2)}
                    {ev().threshold !== null
                      ? ` · ${t("events.threshold", { value: formatNumber(ev().threshold, locale(), 2) })}`
                      : ""}
                  </span>
                  <span class="q">
                    {ev().value === null ? "" : t("events.valueHint")}
                  </span>
                </div>
              </div>
              <div class="grid g-2">
                <section class="card">
                  <div class="card-h">
                    <h2>{t("events.basis")}</h2>
                    <span class="meta">
                      {ev().subjectType === "point"
                        ? place()
                        : t("events.subject.connection")}
                    </span>
                  </div>
                  <div class="card-b stack">
                    <Show
                      when={point()}
                      fallback={
                        <p class="hint" style={{ margin: 0 }}>
                          {t("events.integrationBasis")}{" "}
                          <A
                            href={`/integrations/${encodeURIComponent(ev().subjectId)}`}
                          >
                            {ev().subjectName}
                          </A>
                        </p>
                      }
                    >
                      {(p) => (
                        <Evidence
                          pointId={p().id}
                          startedAt={ev().startedAt}
                          endedAt={ev().endedAt}
                          rule={rule()}
                        />
                      )}
                    </Show>
                    <dl class="kv">
                      <dt>{t("events.subject.label")}</dt>
                      <dd>
                        <Show when={point()} fallback={ev().subjectName}>
                          <A
                            href={`/assets?point=${encodeURIComponent(ev().subjectId)}`}
                          >
                            {ev().subjectName}
                          </A>
                        </Show>
                      </dd>
                      <Show when={rule()}>
                        {(r) => (
                          <>
                            <dt>{t("events.ruleLabel")}</dt>
                            <dd>{conditionText(r())}</dd>
                            <dt>{t("events.notified")}</dt>
                            <dd>
                              {ev().test
                                ? t("events.testNoNotifications")
                                : r().recipients.length > 0
                                  ? `${r()
                                      .recipients.map((id) =>
                                        userName(users(), id),
                                      )
                                      .join(", ")} · ${t("events.channel.app")}`
                                  : t("events.wizard.nobody")}
                            </dd>
                          </>
                        )}
                      </Show>
                      <Show when={!rule()}>
                        <dt>{t("events.ruleLabel")}</dt>
                        <dd>
                          {t(
                            ev().ruleKey === "sys:noData"
                              ? "events.rules.builtInNoData"
                              : "events.rules.builtInIntegration",
                          )}{" "}
                          · {t("events.rules.builtIn")}
                        </dd>
                      </Show>
                    </dl>
                  </div>
                </section>
                <section class="card">
                  <div class="card-h">
                    <h2>{t("events.history")}</h2>
                  </div>
                  <div class="card-b" style={{ display: "grid", gap: "12px" }}>
                    <form
                      style={{ display: "flex", gap: "8px" }}
                      onSubmit={(x) => {
                        x.preventDefault();
                        if (comment().trim()) {
                          act(async () => {
                            await api.events.task([ev().id], {
                              note: comment().trim(),
                            });
                            setComment("");
                          });
                        }
                      }}
                    >
                      <input
                        class="inp"
                        style={{ flex: 1, "min-width": 0 }}
                        placeholder={t("events.commentPlaceholder")}
                        value={comment()}
                        maxLength={2000}
                        onInput={(x) => setComment(x.currentTarget.value)}
                      />
                      <button
                        type="submit"
                        class="btn"
                        disabled={busy() || !comment().trim()}
                      >
                        {t("events.addComment")}
                      </button>
                    </form>
                    <ul class="timeline">
                      <For each={[...detail()!.log].reverse()}>
                        {(l) => (
                          <li>
                            <time>{formatDateTime(l.at, locale())}</time>
                            <span
                              class={`mk ${l.action === "opened" || l.action === "recurred" || l.action === "escalated" ? "crit" : l.action.startsWith("ended") ? "ok" : "acc"}`}
                            />
                            <div>
                              <b style={{ "font-weight": 500 }}>
                                {l.action.startsWith("stage:")
                                  ? t("events.log.stage", {
                                      stage: t(
                                        `events.stage.${l.action.slice(6)}`,
                                      ),
                                    })
                                  : t(`events.log.${l.action}`)}
                              </b>
                              <div class="hint">
                                {l.actor === "system"
                                  ? t("events.system")
                                  : l.actor}
                                {l.note
                                  ? ` · ${l.note === "test" ? t("events.test") : l.note}`
                                  : ""}
                              </div>
                            </div>
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                </section>
              </div>
              <Show when={dialog() === "assign"}>
                <AssignDialog
                  events={[ev()]}
                  onClose={() => setDialog(undefined)}
                  onDone={reload}
                />
              </Show>
              <Show when={dialog() === "close"}>
                <CloseDialog
                  events={[ev()]}
                  onClose={() => setDialog(undefined)}
                  onDone={reload}
                />
              </Show>
              <Show when={dialog() === "rule" && rule()}>
                <RuleWizard
                  rule={rule()}
                  onClose={() => setDialog(undefined)}
                  onSaved={() => {
                    api.invalidate("/api/v1/alert-rules");
                    void refetchRules();
                  }}
                />
              </Show>
            </>
          );
        }}
      </Show>
    </Show>
  );
}

/** The point's values around the event, with the rule's threshold and the time the condition held. */
function Evidence(props: {
  pointId: string;
  startedAt: string;
  endedAt: string | null;
  rule: AlertRule | undefined;
}) {
  const start = () => new Date(props.startedAt).getTime();
  const end = () =>
    props.endedAt ? new Date(props.endedAt).getTime() : Date.now();
  const from = () =>
    new Date(start() - Math.max(6 * HOUR, (end() - start()) / 2));
  const to = () => new Date(Math.min(Date.now(), end() + 2 * HOUR));
  const [data] = createResource(
    () => props.pointId,
    async (id) => {
      const point = siteTree()?.points.find((p) => p.id === id);
      if (!point) {
        return null;
      }
      const reading = (await api.sites.pointsNow(point.nodeId)).find(
        (r) => r.pointId === id,
      );
      return pointSeries(point, reading, from(), to());
    },
  );
  const thresholds = () =>
    props.rule && props.rule.kind !== "noData" && props.rule.threshold !== null
      ? [
          {
            value: props.rule.threshold,
            label: t("events.wizard.thresholdLabel", {
              value: formatNumber(props.rule.threshold, locale(), 2),
            }),
          },
          ...(props.rule.kind === "outside" && props.rule.threshold2 !== null
            ? [
                {
                  value: props.rule.threshold2,
                  label: t("events.wizard.thresholdLabel", {
                    value: formatNumber(props.rule.threshold2, locale(), 2),
                  }),
                },
              ]
            : []),
        ]
      : [];
  return (
    <Show when={data()} fallback={<Skeleton lines={4} />}>
      {(d) => (
        <Chart
          series={[
            {
              key: "v",
              label: d().point.name,
              color: mediumColor(d().point.medium),
              kind: "line",
              points: d().values,
            },
          ]}
          from={from()}
          to={to()}
          unit={unitLabel(d().unit)}
          locale={locale()}
          bucketMs={900_000}
          height={200}
          emptyText={t("overview.noData")}
          thresholds={thresholds()}
          bands={[{ from: start(), to: end() }]}
          now={Date.now()}
          nowLabel={t("overview.nowMark")}
        />
      )}
    </Show>
  );
}
